import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FinanceDashboard } from './FinanceDashboard';
import { IncomeManager } from './IncomeManager';
import { PaymentVerification } from './PaymentVerification';
import { PayrollManager } from './PayrollManager';
import { ReportsCenter } from './ReportsCenter';
import { BillingManager } from './BillingManager';
import {
  BranchIncomeRecord,
  ExpenseRecord,
  PendingPayment,
  OnlineRegistration,
  Employee,
  Tentor,
  ExpenseCategory,
  ProgramPrice,
  DiscountVoucher,
  Student,
  Branch,
} from '../../types/db';
import {
  LayoutDashboard,
  ShieldCheck,
  PlusCircle,
  Banknote,
  Receipt,
  Loader2,
  Save,
  BarChart3,
  Settings,
  UserCheck,
  X,
  Printer,
  DollarSign,
  Tag,
  Landmark,
  Trash2,
  History,
  Wallet,
  Edit3,
  TrendingDown,
  Phone,
  CreditCard,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { getAccountingPeriod } from '../../lib/accountingPeriod';

interface FinanceManagerProps {
  incomes: BranchIncomeRecord[];
  expenses: ExpenseRecord[];
  payments: PendingPayment[];
  registrations: OnlineRegistration[];
  employees: Employee[];
  tentors: Tentor[];
  students: Student[];
  onRefresh: () => void;
  branchId?: string;
  activeBranchName?: string;
}

type FinanceTab =
  | 'OVERVIEW'
  | 'BILLING'
  | 'VERIFY'
  | 'STAFF'
  | 'INCOME'
  | 'EXPENSE'
  | 'PAYROLL'
  | 'SETTINGS'
  | 'REPORTS'
  | 'BANK';

export function FinanceManager({
  incomes,
  expenses,
  payments,
  registrations,
  employees,
  tentors,
  students,
  onRefresh,
  branchId,
  activeBranchName,
}: FinanceManagerProps) {
  const [activeTab, setActiveTab] = useState<FinanceTab>('OVERVIEW');
  const [settingsTab, setSettingsTab] = useState<'PRICING' | 'VOUCHERS'>('PRICING');
  const [loading, setLoading] = useState(false);

  const [prices, setPrices] = useState<ProgramPrice[]>([]);
  const [vouchers, setVouchers] = useState<DiscountVoucher[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);

  const [staffForm, setStaffForm] = useState({
    name: '',
    role: 'STAFF',
    salary: 0,
    phone: '',
    bank_account: '',
  });
  const [editingStaffId, setEditingStaffId] = useState<string | null>(null);

  const [expenseForm, setExpenseForm] = useState({
    description: '',
    amount: 0,
    category: ExpenseCategory.OPERATIONAL,
    date: new Date().toISOString().split('T')[0],
  });
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);

  const [priceForm, setPriceForm] = useState({
    program_type: 'REGULER',
    custom_program: '',
    grade: '5 SD' as string,
    cash_price: 0,
    installment_price: 0,
    installment_durations: '3, 6, 12',
    registration_fee: 50000,
  });
  const [voucherForm, setVoucherForm] = useState({
    code: '',
    discount_percent: 0,
    target_program: 'ALL',
    custom_target: '',
  });
  const [bankForm, setBankForm] = useState({
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    branch_id: branchId || 'HEADQUARTERS',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let priceQuery = supabase.from('program_prices').select('*');
      let voucherQuery = supabase.from('discount_vouchers').select('*');

      if (branchId && branchId !== 'ALL') {
        priceQuery = priceQuery.eq('branch_id', branchId);
        voucherQuery = voucherQuery.eq('branch_id', branchId);
      } else if (!branchId) {
        priceQuery = priceQuery.eq('branch_id', 'HEADQUARTERS');
        voucherQuery = voucherQuery.eq('branch_id', 'HEADQUARTERS');
      }

      const [{ data: pr }, { data: vo }, { data: ba }, { data: br }] = await Promise.all([
        priceQuery,
        voucherQuery,
        supabase.from('bank_accounts').select('*'),
        supabase.from('branches').select('*'),
      ]);

      if (pr) setPrices(pr);
      if (vo) setVouchers(vo);
      if (ba) setBankAccounts(ba);
      if (br) setBranches(br.filter((b: Branch) => b.id !== 'HQ' && b.id !== 'HEADQUARTERS'));
    } catch (e: any) {
      /* console removed */
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  // Optimal Effect: Hanya reload jika loadData (dependency branchId) berubah
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSaveBank = async () => {
    const targetBranch = branchId || 'HEADQUARTERS';
    if (!bankForm.bankName || !bankForm.accountNumber || !bankForm.accountHolder)
      return window.alert('Lengkapi data bank!');

    setLoading(true);
    try {
      const { error } = await supabase.from('bank_accounts').insert({
        bank_name: bankForm.bankName.toUpperCase(),
        account_number: bankForm.accountNumber,
        account_holder: bankForm.accountHolder.toUpperCase(),
        branch_id: targetBranch,
      });
      if (error) throw error;
      window.alert('Informasi Bank Berhasil Disimpan!');
      setBankForm({ bankName: '', accountNumber: '', accountHolder: '', branch_id: targetBranch });
      loadData();
    } catch (err: any) {
      window.alert('Gagal: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBank = async (id: any) => {
    if (!window.confirm('Hapus informasi bank ini?')) return;
    try {
      const { error } = await supabase.from('bank_accounts').delete().eq('id', id);
      if (error) throw error;
      loadData();
    } catch (err: any) {
      alert(err.message);
    }
  };

  const handleAddStaff = async () => {
    if (!branchId || branchId === 'ALL')
      return window.alert('Silakan pilih unit cabang aktif terlebih dahulu!');
    if (!staffForm.name || staffForm.salary <= 0) {
      return window.alert('Lengkapi data staf!');
    }

    setLoading(true);
    try {
      if (editingStaffId) {
        const { error } = await supabase
          .from('employees')
          .update({
            name: staffForm.name.toUpperCase(),
            role: staffForm.role,
            base_salary: staffForm.salary,
            phone_number: staffForm.phone,
            bank_account: staffForm.bank_account.toUpperCase(),
          })
          .eq('id', editingStaffId);
        if (error) throw error;
        window.alert('Data karyawan berhasil diperbarui.');
      } else {
        const { error } = await supabase.from('employees').insert({
          name: staffForm.name.toUpperCase(),
          role: staffForm.role,
          base_salary: staffForm.salary,
          branch_id: branchId,
          phone_number: staffForm.phone,
          bank_account: staffForm.bank_account.toUpperCase(),
        });
        if (error) throw error;
        window.alert('Data karyawan berhasil ditambahkan.');
      }

      setStaffForm({ name: '', role: 'STAFF', salary: 0, phone: '', bank_account: '' });
      setEditingStaffId(null);
      onRefresh();
    } catch (err: any) {
      window.alert('Gagal memproses data staf: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async () => {
    if (!branchId || branchId === 'ALL') return window.alert('Pilih cabang aktif terlebih dahulu!');

    if (!expenseForm.description || expenseForm.amount <= 0 || !expenseForm.date)
      return window.alert('Lengkapi data pengeluaran!');

    setLoading(true);
    try {
      if (editingExpenseId) {
        const { error } = await supabase
          .from('expenses')
          .update({
            description: expenseForm.description,
            amount: expenseForm.amount,
            category: expenseForm.category,
            date: expenseForm.date,
            accounting_period: getAccountingPeriod(expenseForm.date),
          })
          .eq('id', editingExpenseId);
        if (error) throw error;
        window.alert('Pengeluaran berhasil dikoreksi.');
      } else {
        const { error } = await supabase.from('expenses').insert({
          description: expenseForm.description,
          amount: expenseForm.amount,
          category: expenseForm.category,
          branch_id: branchId,
          date: expenseForm.date,
          accounting_period: getAccountingPeriod(expenseForm.date),
        });
        if (error) throw error;
        window.alert('Pengeluaran berhasil dicatat.');
      }

      setExpenseForm({
        description: '',
        amount: 0,
        category: ExpenseCategory.OPERATIONAL,
        date: new Date().toISOString().split('T')[0],
      });
      setEditingExpenseId(null);
      onRefresh();
    } catch (err: any) {
      window.alert('Gagal memproses pengeluaran: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Effect to reset grade when program type changes
  useEffect(() => {
    if (priceForm.program_type === 'UTBK') {
      setPriceForm((prev) => ({ ...prev, grade: '12 SMA' }));
    } else if (priceForm.program_type === 'REGULER') {
      setPriceForm((prev) => ({ ...prev, grade: '5 SD' }));
    } else if (priceForm.program_type === 'TKAA') {
      setPriceForm((prev) => ({ ...prev, grade: 'SD' }));
    }
  }, [priceForm.program_type]);

  const availableGrades = useMemo(() => {
    if (priceForm.program_type === 'REGULER') {
      return ['5 SD', '6 SD', '7 SMP', '8 SMP', '9 SMP', '10 SMA', '11 SMA', '12 SMA'];
    }
    if (priceForm.program_type === 'TKAA') {
      return ['SD', 'SMP', 'SMA'];
    }
    if (priceForm.program_type === 'UTBK') {
      return ['12 SMA'];
    }
    return [
      '5 SD',
      '6 SD',
      '7 SMP',
      '8 SMP',
      '9 SMP',
      '10 SMA',
      '11 SMA',
      '12 SMA',
      'SD',
      'SMP',
      'SMA',
      '12 SMA',
    ];
  }, [priceForm.program_type]);

  const handleSavePrice = async () => {
    if (!branchId || branchId === 'ALL') return window.alert('Pilih cabang aktif!');
    const finalProgram =
      priceForm.program_type === 'INPUT MANUAL' ? priceForm.custom_program : priceForm.program_type;
    if (!finalProgram || priceForm.cash_price <= 0)
      return window.alert('Mohon lengkapi nama program dan nominal harga!');

    setLoading(true);
    try {
      const durations = priceForm.installment_durations
        .split(',')
        .map((d) => parseInt(d.trim()))
        .filter((d) => !isNaN(d));
      const { error } = await supabase.from('program_prices').upsert({
        program_name: finalProgram.toUpperCase(),
        grade: priceForm.grade,
        price: priceForm.cash_price, // Tetap simpan ke kolom price lama untuk kompatibilitas
        cash_price: priceForm.cash_price,
        installment_price: priceForm.installment_price,
        installment_durations: durations,
        registration_fee: priceForm.registration_fee,
        branch_id: branchId,
      });
      if (error) throw error;
      window.alert('Kombinasi harga program berhasil disimpan!');
      loadData();
    } catch (e: any) {
      window.alert('Gagal menyimpan harga: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVoucher = async () => {
    if (!branchId || branchId === 'ALL') return window.alert('Pilih cabang aktif!');
    const finalTarget =
      voucherForm.target_program === 'INPUT MANUAL'
        ? voucherForm.custom_target
        : voucherForm.target_program;
    if (!voucherForm.code || voucherForm.discount_percent <= 0)
      return window.alert('Lengkapi data voucher!');
    setLoading(true);
    try {
      const { error } = await supabase.from('discount_vouchers').insert({
        code: voucherForm.code.toUpperCase(),
        discount_percent: voucherForm.discount_percent,
        target_program: finalTarget,
        branch_id: branchId,
        is_active: true,
      });
      if (error) throw error;
      window.alert('Voucher berhasil diaktifkan!');
      setVoucherForm({ code: '', discount_percent: 0, target_program: 'ALL', custom_target: '' });
      loadData();
    } catch (err: any) {
      window.alert('Gagal menyimpan voucher: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const printSlip = (emp: Employee) => {
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><script src="https://cdn.tailwindcss.com"></script></head><body class="p-10 font-sans text-slate-900">
        <div class="border-4 border-slate-900 p-12 max-w-2xl mx-auto rounded-[3rem] shadow-2xl bg-white relative overflow-hidden">
          <div class="flex justify-between items-start mb-10 border-b-2 border-slate-100 pb-8">
            <div>
              <h1 class="text-3xl font-black uppercase tracking-tighter">Bimbel DELTA</h1>
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-1">Official Payroll Document</p>
            </div>
            <div class="text-right">
              <p class="text-[10px] font-black text-indigo-600 uppercase tracking-widest">ID Slip: PAY-${Date.now()}</p>
              <p class="font-bold text-sm mt-1">${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-12 mb-12">
            <div class="space-y-4">
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b pb-2">Penerima</p>
              <div><p class="text-[10px] font-bold text-slate-400">Nama</p><p class="font-black text-lg uppercase">${emp.name}</p></div>
              <div><p class="text-[10px] font-bold text-slate-400">Jabatan</p><p class="font-black uppercase">${emp.role}</p></div>
              <div><p class="text-[10px] font-bold text-slate-400">Rekening</p><p class="font-black font-mono">${emp.bank_account || 'KAS TUNAI'}</p></div>
            </div>
            <div class="space-y-4">
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest border-b pb-2">Keuangan</p>
              <div class="flex justify-between items-end"><p class="text-[10px] font-bold text-slate-400">Gaji Pokok</p><p class="font-black">Rp ${emp.base_salary?.toLocaleString()}</p></div>
              <div class="pt-4 border-t-2 border-slate-900 flex justify-between items-end mt-4"><p class="font-black uppercase text-sm">Total</p><p class="text-xl font-black text-indigo-600">Rp ${emp.base_salary?.toLocaleString()}</p></div>
            </div>
          </div>
          <div class="mt-20 grid grid-cols-2 gap-20">
            <div class="text-center"><p class="text-xs font-bold mb-16">Finance Admin,</p><div class="border-t-2 border-slate-900 pt-2 font-black uppercase text-[10px]">( _______________ )</div></div>
            <div class="text-center"><p class="text-xs font-bold mb-16">Penerima,</p><div class="border-t-2 border-slate-900 pt-2 font-black uppercase text-[10px]">( ${emp.name} )</div></div>
          </div>
        </div>
      </body></html>
    `);
    win.document.close();
    win.print();
  };

  // ✅ FILTER: Karyawan per cabang
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => branchId === 'ALL' || emp.branch_id === branchId);
  }, [employees, branchId]);

  // ✅ FILTER: Rekening per cabang
  const filteredBankAccounts = useMemo(() => {
    return bankAccounts.filter((b) => branchId === 'ALL' || b.branch_id === branchId);
  }, [bankAccounts, branchId]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter((exp) => branchId === 'ALL' || exp.branch_id === branchId).slice(0, 50);
  }, [expenses, branchId]);

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      <div className="flex bg-white p-1.5 rounded-2xl border border-slate-100 shadow-sm w-full overflow-x-auto no-scrollbar">
        {[
          { id: 'OVERVIEW', label: 'Laba Rugi', icon: <LayoutDashboard className="w-4 h-4" /> },
          { id: 'BILLING', label: 'Piutang', icon: <Wallet className="w-4 h-4" /> },
          { id: 'VERIFY', label: 'Verifikasi', icon: <ShieldCheck className="w-4 h-4" /> },
          { id: 'INCOME', label: 'Input Pemasukan', icon: <PlusCircle className="w-4 h-4" /> },
          { id: 'EXPENSE', label: 'Input Pengeluaran', icon: <Receipt className="w-4 h-4" /> },
          { id: 'STAFF', label: 'Staf Unit', icon: <UserCheck className="w-4 h-4" /> },
          { id: 'PAYROLL', label: 'Payroll', icon: <Banknote className="w-4 h-4" /> },
          { id: 'REPORTS', label: 'Laporan', icon: <BarChart3 className="w-4 h-4" /> },
          { id: 'BANK', label: 'Bank Info', icon: <Landmark className="w-4 h-4" /> },
          { id: 'SETTINGS', label: 'Config', icon: <Settings className="w-4 h-4" /> },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id as FinanceTab)}
            className={`px-6 py-3 rounded-xl font-black text-[9px] uppercase tracking-widest flex items-center gap-2 shrink-0 transition-all ${activeTab === t.id ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-100' : 'text-slate-400 hover:bg-slate-50'}`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white p-8 md:p-10 rounded-[3rem] border border-slate-50 shadow-sm min-h-[600px]">
        {activeTab === 'OVERVIEW' && (
          <FinanceDashboard
            incomes={incomes}
            expenses={expenses}
            students={students}
            branchId={branchId || 'ALL'}
          />
        )}

        {activeTab === 'BILLING' && <BillingManager students={students} onRefresh={onRefresh} />}

        {activeTab === 'VERIFY' && (
          <PaymentVerification
            payments={payments}
            registrations={registrations}
            onRefresh={onRefresh}
          />
        )}

        {activeTab === 'INCOME' && (
          <IncomeManager
            branchId={branchId || 'ALL'}
            incomes={incomes}
            onSave={async (data) => {
              if (!branchId || branchId === 'ALL')
                throw new Error('Silakan pilih unit cabang terlebih dahulu');
              const { error } = await supabase.from('branch_incomes').insert({
                ...data,
                branch_id: branchId,
                accounting_period: getAccountingPeriod(data.date),
              });
              if (error) throw error;
              onRefresh();
            }}
            onUpdate={async (id, data) => {
              const { error } = await supabase
                .from('branch_incomes')
                .update({
                  ...data,
                  accounting_period: getAccountingPeriod(data.date),
                })
                .eq('id', id);
              if (error) throw error;
              onRefresh();
            }}
          />
        )}

        {activeTab === 'EXPENSE' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fade-in">
            <div
              className={`p-10 rounded-[3rem] border transition-all shadow-sm space-y-8 h-fit ${editingExpenseId ? 'bg-indigo-50/50 border-indigo-200' : 'bg-white border-slate-100'}`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm ${editingExpenseId ? 'bg-indigo-600 text-white' : 'bg-rose-100 text-rose-600'}`}
                >
                  {editingExpenseId ? (
                    <Edit3 className="w-6 h-6" />
                  ) : (
                    <Receipt className="w-6 h-6" />
                  )}
                </div>
                <div>
                  <h3 className="font-black text-2xl text-slate-900 uppercase tracking-tight leading-none">
                    {editingExpenseId ? 'Koreksi Expense' : 'Input Expense'}
                  </h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                    {editingExpenseId
                      ? 'Memperbaiki riwayat pengeluaran'
                      : 'Pencatatan Pengeluaran Kas Unit'}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                    Tanggal
                  </label>
                  <input
                    type="date"
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none font-bold bg-slate-50/50 text-slate-800"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm({ ...expenseForm, date: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                    Keterangan
                  </label>
                  <input
                    placeholder="Misal: Listrik Januari 2024"
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none font-bold bg-slate-50/50 text-slate-800"
                    value={expenseForm.description}
                    onChange={(e) =>
                      setExpenseForm({ ...expenseForm, description: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                    Nominal (Rp)
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl outline-none font-black text-2xl text-rose-600 bg-slate-50/50"
                    value={expenseForm.amount}
                    onChange={(e) =>
                      setExpenseForm({ ...expenseForm, amount: Number(e.target.value) })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                    Kategori
                  </label>
                  <select
                    className="w-full border-2 border-slate-50 p-4 rounded-2xl font-black uppercase text-xs bg-slate-50/50 text-slate-700 outline-none focus:border-rose-300"
                    value={expenseForm.category}
                    onChange={(e) =>
                      setExpenseForm({ ...expenseForm, category: e.target.value as any })
                    }
                  >
                    {Object.values(ExpenseCategory).map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3">
                  {editingExpenseId && (
                    <button
                      onClick={() => {
                        setEditingExpenseId(null);
                        setExpenseForm({
                          description: '',
                          amount: 0,
                          category: ExpenseCategory.OPERATIONAL,
                          date: new Date().toISOString().split('T')[0],
                        });
                      }}
                      className="flex-1 bg-slate-200 text-slate-600 py-6 rounded-[2rem] font-black uppercase tracking-widest hover:bg-slate-300 transition-all flex justify-center items-center gap-2"
                    >
                      <X className="w-5 h-5" /> BATAL
                    </button>
                  )}
                  <button
                    onClick={handleAddExpense}
                    disabled={loading}
                    className={`flex-[2] py-6 rounded-[2rem] font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all ${editingExpenseId ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-slate-900 text-white hover:bg-black'}`}
                  >
                    {loading ? (
                      <Loader2 className="animate-spin w-5 h-5 mx-auto" />
                    ) : editingExpenseId ? (
                      'UPDATE DATA'
                    ) : (
                      'SIMPAN PENGELUARAN'
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-8 rounded-[3.5rem] border border-slate-100 flex flex-col h-[600px]">
              <div className="flex items-center justify-between mb-6 px-4">
                <div className="flex items-center gap-3">
                  <TrendingDown className="text-rose-400 w-5 h-5" />
                  <h4 className="font-black text-slate-800 uppercase text-xs tracking-widest">
                    Log Expense Terkini
                  </h4>
                </div>
                <span className="text-[8px] font-black text-rose-600 bg-white px-3 py-1 rounded-full border border-slate-200 uppercase tracking-widest">
                  Unit Filtered
                </span>
              </div>

              <div className="flex-1 overflow-y-auto pr-2 no-scrollbar space-y-3">
                {filteredExpenses.map((log, idx) => (
                  <div
                    key={idx}
                    className={`bg-white p-5 rounded-3xl border flex justify-between items-center group transition-all shadow-sm ${editingExpenseId === log.id ? 'border-indigo-500 ring-2 ring-indigo-100' : 'border-slate-100 hover:border-rose-200'}`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs ${editingExpenseId === log.id ? 'bg-indigo-600 text-white' : 'bg-rose-50 text-rose-500'}`}
                      >
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-black text-slate-800 uppercase text-[11px] truncate max-w-[150px]">
                          {log.description}
                        </p>
                        <p className="text-[9px] text-slate-400 font-bold uppercase mt-0.5">
                          {log.date} • {log.category}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-black text-rose-600 text-sm">
                          Rp {Number(log.amount).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => {
                            setEditingExpenseId(log.id);
                            setExpenseForm({
                              description: log.description,
                              amount: log.amount,
                              category: log.category,
                              date: log.date,
                            });
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-600 hover:text-white transition-all"
                          title="Edit / Koreksi"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {filteredExpenses.length === 0 && (
                  <div className="h-full flex flex-col items-center justify-center text-slate-300 opacity-50">
                    <History className="w-12 h-12 mb-4" />
                    <p className="font-black uppercase tracking-widest text-[10px]">
                      Belum ada riwayat pengeluaran
                    </p>
                  </div>
                )}
              </div>

              <div className="pt-6 mt-6 border-t border-slate-200 text-center">
                <p className="text-[8px] font-bold text-slate-400 uppercase tracking-[0.3em]">
                  Integrity Management Engine v2.0
                </p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'STAFF' && (
          <div className="space-y-10 animate-fade-in">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <h3 className="text-2xl font-black uppercase tracking-tight text-slate-800">
                Manajemen Karyawan
              </h3>
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] bg-slate-50 px-4 py-2 rounded-full border border-slate-100">
                Unit: {activeBranchName || 'Pusat'}
              </span>
            </div>

            <div
              className={`p-8 rounded-[3rem] border shadow-inner space-y-8 transition-all ${editingStaffId ? 'bg-indigo-50/50 border-indigo-200' : 'bg-slate-50 border-slate-100'}`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm border border-slate-100 ${editingStaffId ? 'bg-indigo-600 text-white' : 'bg-white text-indigo-600'}`}
                >
                  {editingStaffId ? (
                    <Edit3 className="w-6 h-6" />
                  ) : (
                    <UserCheck className="w-6 h-6" />
                  )}
                </div>
                <h4 className="font-black text-slate-800 uppercase text-xs tracking-widest">
                  {editingStaffId ? 'Edit Data Karyawan' : 'Input Data Karyawan Baru'}
                </h4>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                    Nama Lengkap
                  </label>
                  <input
                    placeholder="Nama"
                    className="w-full border-2 border-white p-4 rounded-2xl text-xs font-bold bg-white outline-none focus:border-indigo-200 transition-all shadow-sm"
                    value={staffForm.name}
                    onChange={(e) =>
                      setStaffForm({ ...staffForm, name: e.target.value.toUpperCase() })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                    Jabatan
                  </label>
                  <select
                    className="w-full border-2 border-white p-4 rounded-2xl text-[10px] font-black bg-white outline-none focus:border-indigo-200 transition-all shadow-sm cursor-pointer uppercase"
                    value={staffForm.role}
                    onChange={(e) => setStaffForm({ ...staffForm, role: e.target.value })}
                  >
                    <option value="STAFF">Staff</option>
                    <option value="MARKETING">Marketing</option>
                    <option value="FINANCE">Finance</option>
                    <option value="ACADEMIC">Academic</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                    Gaji Pokok (Rp)
                  </label>
                  <input
                    type="number"
                    placeholder="0"
                    className="w-full border-2 border-white p-4 rounded-2xl text-xs font-bold bg-white outline-none focus:border-indigo-200 transition-all shadow-sm"
                    value={staffForm.salary}
                    onChange={(e) => setStaffForm({ ...staffForm, salary: Number(e.target.value) })}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                    No. WhatsApp
                  </label>
                  <input
                    placeholder="08..."
                    className="w-full border-2 border-white p-4 rounded-2xl text-xs font-bold bg-white outline-none focus:border-indigo-200 transition-all shadow-sm"
                    value={staffForm.phone}
                    onChange={(e) => setStaffForm({ ...staffForm, phone: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                    No. Rekening
                  </label>
                  <input
                    placeholder="Rekening Bank"
                    className="w-full border-2 border-white p-4 rounded-2xl text-xs font-bold bg-white outline-none focus:border-indigo-200 transition-all shadow-sm"
                    value={staffForm.bank_account}
                    onChange={(e) =>
                      setStaffForm({ ...staffForm, bank_account: e.target.value.toUpperCase() })
                    }
                  />
                </div>
              </div>

              <div className="flex gap-3">
                {editingStaffId && (
                  <button
                    onClick={() => {
                      setEditingStaffId(null);
                      setStaffForm({
                        name: '',
                        role: 'STAFF',
                        salary: 0,
                        phone: '',
                        bank_account: '',
                      });
                    }}
                    className="flex-1 bg-slate-200 text-slate-600 py-5 rounded-[2rem] font-black text-[10px] uppercase tracking-[0.2em] hover:bg-slate-300 transition-all"
                  >
                    BATAL
                  </button>
                )}
                <button
                  onClick={handleAddStaff}
                  disabled={loading}
                  className={`flex-[2] py-5 rounded-[2rem] font-black text-[10px] uppercase tracking-[0.2em] shadow-xl hover:bg-indigo-700 transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-4 ${editingStaffId ? 'bg-indigo-600 text-white' : 'bg-indigo-600 text-white'}`}
                >
                  {loading ? (
                    <Loader2 className="animate-spin w-5 h-5" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  {editingStaffId ? 'PERBARUI DATA KARYAWAN' : 'SIMPAN & AKTIFKAN DATA KARYAWAN'}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEmployees.map((emp) => (
                <div
                  key={emp.id}
                  className={`p-10 bg-white border rounded-[3rem] shadow-sm relative overflow-hidden group hover:shadow-xl transition-all border-b-8 ${editingStaffId === emp.id ? 'border-indigo-500 ring-2 ring-indigo-50' : 'border-slate-100 border-b-indigo-500/20'}`}
                >
                  <div className="flex justify-between mb-6">
                    <div
                      className={`w-16 h-16 rounded-[1.5rem] flex items-center justify-center font-black text-2xl uppercase shadow-inner group-hover:bg-indigo-600 group-hover:text-white transition-all ${editingStaffId === emp.id ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600'}`}
                    >
                      {emp.name.charAt(0)}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => {
                          setEditingStaffId(emp.id);
                          setStaffForm({
                            name: emp.name,
                            role: emp.role as string,
                            salary: emp.base_salary || 0,
                            phone: emp.phone_number || '',
                            bank_account: emp.bank_account || '',
                          });
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="text-slate-200 hover:text-indigo-600 p-2 transition-colors"
                        title="Edit Data"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => printSlip(emp)}
                        className="text-slate-200 hover:text-indigo-600 p-2 transition-colors"
                        title="Cetak Slip"
                      >
                        <Printer className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Hapus data staf ini?')) return;
                          await supabase.from('employees').delete().eq('id', emp.id);
                          onRefresh();
                        }}
                        className="text-slate-200 hover:text-rose-500 p-2 transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <h4 className="font-black text-xl uppercase mb-1 text-slate-800 tracking-tight">
                    {emp.name}
                  </h4>
                  <p className="text-indigo-600 font-black uppercase tracking-widest mb-6 text-[9px] bg-indigo-50 px-2.5 py-1 rounded-lg w-fit">
                    {emp.role}
                  </p>

                  <div className="space-y-3 mb-8">
                    <p className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-3">
                      <Phone className="w-3.5 h-3.5 text-indigo-300" /> {emp.phone_number || '—'}
                    </p>
                    <p className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-3">
                      <CreditCard className="w-3.5 h-3.5 text-indigo-300" />{' '}
                      {emp.bank_account || '—'}
                    </p>
                  </div>

                  <div className="flex justify-between items-center pt-6 border-t-2 border-slate-50">
                    <div>
                      <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest">
                        Gaji Pokok
                      </p>
                      <p className="font-black text-xl text-slate-900 tracking-tighter">
                        Rp {emp.base_salary?.toLocaleString()}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-slate-50 rounded-full flex items-center justify-center text-slate-200 group-hover:text-indigo-200 transition-colors">
                      <History className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              ))}
              {filteredEmployees.length === 0 && (
                <div className="col-span-full py-40 text-center border-4 border-dashed rounded-[3rem] border-slate-50 text-slate-200 uppercase font-black tracking-widest text-xs">
                  Belum Ada Karyawan Terdaftar Di Unit Ini
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'PAYROLL' && (
          <PayrollManager tentors={tentors} branchId={branchId || 'HEADQUARTERS'} />
        )}

        {activeTab === 'REPORTS' && (
          <ReportsCenter
            branchId={branchId || 'ALL'}
            branchName={activeBranchName || 'Konsolidasi Jaringan'}
          />
        )}

        {activeTab === 'BANK' && (
          <div className="space-y-10 animate-fade-in">
            <div className="max-w-xl bg-slate-50 p-10 rounded-[3rem] border border-slate-100 space-y-6">
              <div className="flex items-center gap-3 mb-4">
                <Landmark className="text-indigo-600 w-6 h-6" />
                <h3 className="font-black text-xl uppercase tracking-tight">
                  Tambah Rekening Unit
                </h3>
              </div>
              <input
                className="w-full border-2 border-white p-4 rounded-2xl font-bold bg-white outline-none"
                placeholder="Nama Bank"
                value={bankForm.bankName}
                onChange={(e) =>
                  setBankForm({ ...bankForm, bankName: e.target.value.toUpperCase() })
                }
              />
              <input
                className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white outline-none"
                placeholder="Nomor Rekening"
                value={bankForm.accountNumber}
                onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })}
              />
              <input
                className="w-full border-2 border-white p-4 rounded-2xl font-bold bg-white outline-none"
                placeholder="Atas Nama"
                value={bankForm.accountHolder}
                onChange={(e) =>
                  setBankForm({ ...bankForm, accountHolder: e.target.value.toUpperCase() })
                }
              />
              <select
                className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-xs outline-none"
                value={bankForm.branch_id}
                onChange={(e) => setBankForm({ ...bankForm, branch_id: e.target.value })}
              >
                <option value="HEADQUARTERS">KANTOR PUSAT</option>
                {branches
                  .filter((b) => b.is_active !== false || b.id === bankForm.branch_id)
                  .map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name.toUpperCase()}
                      {b.is_active === false ? ' (NONAKTIF)' : ''}
                    </option>
                  ))}
              </select>
              <button
                onClick={handleSaveBank}
                disabled={loading}
                className="w-full bg-indigo-600 text-white py-5 rounded-3xl font-black uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all"
              >
                SIMPAN REKENING
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredBankAccounts.map((b) => (
                <div
                  key={b.id}
                  className="p-8 bg-white border-2 border-slate-50 rounded-[3rem] relative overflow-hidden group"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 rounded-bl-full -mr-12 -mt-12 opacity-50"></div>
                  <div className="flex items-center gap-5 mb-8">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-indigo-600 shadow-sm border border-slate-100">
                      <Landmark className="w-7 h-7" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] mb-1">
                        {b.bank_name}
                      </p>
                      <p className="font-bold text-slate-400 text-[10px] uppercase">
                        Unit: {b.branch_id}
                      </p>
                    </div>
                  </div>
                  <p className="text-3xl font-black tracking-widest font-mono mb-2">
                    {b.account_number}
                  </p>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                    {b.account_holder}
                  </p>
                  <button
                    onClick={() => handleDeleteBank(b.id)}
                    className="absolute bottom-8 right-8 text-slate-200 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              ))}
              {filteredBankAccounts.length === 0 && (
                <div className="col-span-full py-20 text-center border-4 border-dashed rounded-[3rem] border-slate-50 text-slate-200 uppercase font-black tracking-widest text-xs">
                  Belum Ada Rekening Di Unit Ini
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'SETTINGS' && (
          <div className="space-y-12 animate-fade-in">
            <div className="flex gap-4 border-b pb-4">
              <button
                onClick={() => setSettingsTab('PRICING')}
                className={`font-black text-xs uppercase tracking-widest ${settingsTab === 'PRICING' ? 'text-indigo-600 border-b-2 border-indigo-600 pb-4 -mb-4' : 'text-slate-400'}`}
              >
                Price Matrix
              </button>
              <button
                onClick={() => setSettingsTab('VOUCHERS')}
                className={`font-black text-xs uppercase tracking-widest ${settingsTab === 'VOUCHERS' ? 'text-indigo-600 border-b-2 border-indigo-600 pb-4 -mb-4' : 'text-slate-400'}`}
              >
                Discount Vouchers
              </button>
            </div>

            {settingsTab === 'PRICING' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-1 bg-slate-50 p-10 rounded-[3rem] border border-slate-100 space-y-6">
                  <h4 className="font-black text-lg uppercase tracking-tight flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-indigo-600" /> Setup Harga
                  </h4>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                      Pilih Program
                    </label>
                    <select
                      className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-xs outline-none focus:border-indigo-200"
                      value={priceForm.program_type}
                      onChange={(e) => setPriceForm({ ...priceForm, program_type: e.target.value })}
                    >
                      <option value="REGULER">REGULER</option>
                      <option value="UTBK">UTBK</option>
                      <option value="TKAA">TKAA</option>
                      <option value="INPUT MANUAL">MANUAL / CUSTOM</option>
                    </select>
                  </div>
                  {priceForm.program_type === 'INPUT MANUAL' && (
                    <input
                      className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-xs uppercase outline-none focus:border-indigo-200"
                      placeholder="Nama Program..."
                      value={priceForm.custom_program}
                      onChange={(e) =>
                        setPriceForm({ ...priceForm, custom_program: e.target.value })
                      }
                    />
                  )}

                  {priceForm.program_type !== 'UTBK' && (
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                        Pilih Kelas
                      </label>
                      <select
                        className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-xs outline-none focus:border-indigo-200"
                        value={priceForm.grade}
                        onChange={(e) => setPriceForm({ ...priceForm, grade: e.target.value })}
                      >
                        {availableGrades.map((g) => (
                          <option key={g} value={g}>
                            {g}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                        Harga Cash (Rp)
                      </label>
                      <input
                        type="number"
                        className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-lg outline-none focus:border-indigo-200"
                        placeholder="0"
                        value={priceForm.cash_price}
                        onChange={(e) =>
                          setPriceForm({ ...priceForm, cash_price: Number(e.target.value) })
                        }
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-amber-600 uppercase ml-2 tracking-widest">
                        Harga Angsur (Rp)
                      </label>
                      <input
                        type="number"
                        className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-lg outline-none focus:border-amber-200"
                        placeholder="0"
                        value={priceForm.installment_price}
                        onChange={(e) =>
                          setPriceForm({ ...priceForm, installment_price: Number(e.target.value) })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase ml-2 tracking-widest">
                      Pilihan Tenor (Pisahkan dengan koma)
                    </label>
                    <input
                      className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-xs outline-none focus:border-indigo-200"
                      placeholder="3, 6, 12"
                      value={priceForm.installment_durations}
                      onChange={(e) =>
                        setPriceForm({ ...priceForm, installment_durations: e.target.value })
                      }
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-emerald-600 uppercase ml-2 tracking-widest">
                      Uang Pendaftaran (Rp)
                    </label>
                    <input
                      type="number"
                      className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-lg outline-none focus:border-emerald-200"
                      placeholder="50000"
                      value={priceForm.registration_fee}
                      onChange={(e) =>
                        setPriceForm({ ...priceForm, registration_fee: Number(e.target.value) })
                      }
                    />
                  </div>

                  <button
                    onClick={handleSavePrice}
                    disabled={loading}
                    className="w-full bg-indigo-600 text-white py-6 rounded-[2rem] font-black uppercase text-xs tracking-[0.2em] shadow-2xl hover:bg-indigo-700 active:scale-95 transition-all"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}{' '}
                    UPDATE MATRIX
                  </button>
                </div>
                <div className="lg:col-span-2 space-y-4">
                  <h4 className="font-black text-slate-400 uppercase text-[10px] tracking-widest ml-2">
                    Daftar Harga Unit {branchId || 'Pusat'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {prices.map((p, idx) => (
                      <div
                        key={idx}
                        className="p-8 bg-white border border-slate-100 rounded-[2.5rem] flex flex-col gap-4 group relative overflow-hidden shadow-sm hover:border-indigo-200 transition-all"
                      >
                        <div className="flex justify-between items-start relative z-10">
                          <div>
                            <p className="font-black text-indigo-900 uppercase text-xs tracking-tight">
                              {p.program_name}
                            </p>
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                              Grade: {p.grade}
                            </p>
                          </div>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Hapus harga ini?')) return;
                              await supabase.from('program_prices').delete().match({
                                program_name: p.program_name,
                                grade: p.grade,
                                branch_id: p.branch_id,
                              });
                              loadData();
                            }}
                            className="text-rose-100 group-hover:text-rose-400 p-2 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4 relative z-10">
                          <div className="bg-indigo-50/50 p-4 rounded-2xl">
                            <p className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-1">
                              CASH
                            </p>
                            <p className="font-black text-indigo-600 text-sm">
                              Rp {p.cash_price?.toLocaleString() || p.price?.toLocaleString()}
                            </p>
                          </div>
                          <div className="bg-amber-50/50 p-4 rounded-2xl">
                            <p className="text-[8px] font-black text-amber-400 uppercase tracking-widest mb-1">
                              ANGSUR
                            </p>
                            <p className="font-black text-amber-600 text-sm">
                              Rp {p.installment_price?.toLocaleString() || '—'}
                            </p>
                          </div>
                        </div>

                        <div className="flex justify-between items-center relative z-10">
                          <div className="flex gap-1">
                            {Array.isArray(p.installment_durations) &&
                              p.installment_durations.map((d) => (
                                <span
                                  key={d}
                                  className="text-[7px] font-black bg-slate-100 text-slate-400 px-2 py-0.5 rounded-md"
                                >
                                  {d}X
                                </span>
                              ))}
                          </div>
                          <p className="text-[8px] font-black text-emerald-500 uppercase tracking-widest">
                            Regis: Rp {p.registration_fee?.toLocaleString()}
                          </p>
                        </div>

                        <div className="absolute -bottom-6 -left-6 w-16 h-16 bg-slate-50 rounded-full opacity-50"></div>
                      </div>
                    ))}
                    {prices.length === 0 && (
                      <div className="col-span-full py-20 text-center border-4 border-dashed rounded-[3rem] border-slate-50 text-slate-200 font-black uppercase text-xs tracking-widest">
                        Belum Ada Matrix Harga
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {settingsTab === 'VOUCHERS' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                <div className="lg:col-span-1 bg-slate-50 p-10 rounded-[3rem] border border-slate-100 space-y-6">
                  <h4 className="font-black text-lg uppercase tracking-tight flex items-center gap-2">
                    <Tag className="w-5 h-5 text-emerald-600" /> Generate Voucher
                  </h4>
                  <input
                    className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-sm uppercase outline-none focus:border-emerald-200"
                    placeholder="KODE VOUCHER"
                    value={voucherForm.code}
                    onChange={(e) =>
                      setVoucherForm({ ...voucherForm, code: e.target.value.toUpperCase() })
                    }
                  />
                  <input
                    type="number"
                    className="w-full border-2 border-white p-4 rounded-2xl font-black bg-white text-lg outline-none focus:border-emerald-200"
                    placeholder="Diskon %"
                    value={voucherForm.discount_percent}
                    onChange={(e) =>
                      setVoucherForm({ ...voucherForm, discount_percent: Number(e.target.value) })
                    }
                  />
                  <button
                    onClick={handleSaveVoucher}
                    disabled={loading}
                    className="w-full bg-emerald-600 text-white py-6 rounded-[2rem] font-black uppercase text-xs tracking-widest shadow-2xl hover:bg-emerald-700 active:scale-95 transition-all"
                  >
                    AKTIFKAN VOUCHER
                  </button>
                </div>
                <div className="lg:col-span-2 space-y-4">
                  {vouchers.map((v) => (
                    <div
                      key={v.id}
                      className="p-8 bg-white border-2 border-dashed border-emerald-100 rounded-[2.5rem] flex justify-between items-center shadow-sm"
                    >
                      <div className="flex items-center gap-6">
                        <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center font-black text-xs shadow-sm border border-emerald-100">
                          <Tag className="w-7 h-7" />
                        </div>
                        <div>
                          <p className="font-black text-indigo-900 tracking-widest text-lg uppercase">
                            {v.code}
                          </p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                            OFF {v.discount_percent}% • ALL PROGRAM
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm('Hapus voucher?')) return;
                          await supabase.from('discount_vouchers').delete().eq('id', v.id);
                          loadData();
                        }}
                        className="text-rose-200 hover:text-rose-600 p-3 rounded-xl hover:bg-rose-50 transition-all"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                  {vouchers.length === 0 && (
                    <div className="py-20 text-center border-4 border-dashed rounded-[3rem] border-slate-50 text-slate-200 font-black uppercase text-xs tracking-widest">
                      Belum Ada Voucher Aktif
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
